// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ConfirmationTypesEnum } from '@shared/utils/Trading/ConfirmationTypesEnum';
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { DataCache } from '../DataCache';
import { Resources } from '@shared/localizations/Resources';
import { BaseSettings } from '../Settings/BaseGeneralSettingsWrapper';
export const DISPLAY_QUANTITY_IN_LOTS_V = 'property.general.showLotsFX';
export class _BaseGeneralSettingsUtils {
    displayAmountInLots() {
        if (!Resources.isHidden(DISPLAY_QUANTITY_IN_LOTS_V)) {
            return BaseSettings.isQuantityInLots;
        }
        return false;
    }
    existTradingSymbolIdOrFirst(symbolID) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isNullOrUndefined(symbolID)) {
                const foundSymbol = yield DataCache.getInstrumentByInteriorIdAsync(symbolID);
                if (!isNullOrUndefined(foundSymbol)) {
                    return foundSymbol.GetInteriorID();
                }
            }
            const firstSymbol = DataCache.getFirstTradableInstrument();
            if (!isNullOrUndefined(firstSymbol)) {
                return firstSymbol.GetInteriorID();
            }
            return null;
        });
    }
    updateConfirmation(operationType, showConfirmation) {
        if (operationType === undefined || showConfirmation === undefined) {
            return;
        }
        switch (operationType) {
            case ConfirmationTypesEnum.OrderCancel:
                if (BaseSettings.confirmOrderCancel !== showConfirmation) {
                    BaseSettings.confirmOrderCancel = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.OrderPlace:
                if (BaseSettings.confirmOrderSending !== showConfirmation) {
                    BaseSettings.confirmOrderSending = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.OrderPlaceAlert:
                if (BaseSettings.confirmPlaceAlert !== showConfirmation) {
                    BaseSettings.confirmPlaceAlert = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.AlertCancel:
                if (BaseSettings.confirmCancelAlert !== showConfirmation) {
                    BaseSettings.confirmCancelAlert = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.PositionClose:
                if (BaseSettings.confirmPositionClosing !== showConfirmation) {
                    BaseSettings.confirmPositionClosing = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.PositionReverse:
                if (BaseSettings.confirmReversePosition !== showConfirmation) {
                    BaseSettings.confirmReversePosition = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.Modify:
                if (BaseSettings.confirmOrderPositionsModification !== showConfirmation) {
                    BaseSettings.confirmOrderPositionsModification = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.DealTickets:
                if (BaseSettings.openDialTicketsOnWorkspace !== showConfirmation) {
                    BaseSettings.openDialTicketsOnWorkspace = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.ShowMarginDealTicketsOnWorkspace:
                if (BaseSettings.showMarginDealTicketOnWorkspace !== showConfirmation) {
                    BaseSettings.showMarginDealTicketOnWorkspace = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.PositionSizeCalculatorInfoWindow:
                if (BaseSettings.positionSizeCalculatorInfoWindow !== showConfirmation) {
                    BaseSettings.positionSizeCalculatorInfoWindow = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.SubscriptionsAndUnsubscriptions:
                if (BaseSettings.confirmSubscriptions !== showConfirmation) {
                    BaseSettings.confirmSubscriptions = showConfirmation;
                }
                break;
            case ConfirmationTypesEnum.CopyTradingStartStopFollow:
                if (BaseSettings.confirmAccountFollowing !== showConfirmation) {
                    BaseSettings.confirmAccountFollowing = showConfirmation;
                }
                break;
        }
    }
    useConfirmation(type) {
        const confirmTypes = ConfirmationTypesEnum;
        switch (type) {
            case confirmTypes.OrderCancel:
                return BaseSettings.confirmOrderCancel;
            case confirmTypes.OrderPlace:
                return BaseSettings.confirmOrderSending;
            case confirmTypes.PositionClose:
                return BaseSettings.confirmPositionClosing;
            case confirmTypes.Modify:
                return BaseSettings.confirmOrderPositionsModification;
            case confirmTypes.DealTickets:
                return BaseSettings.openDialTicketsOnWorkspace;
            case confirmTypes.ShowMarginDealTicketsOnWorkspace:
                return BaseSettings.showMarginDealTicketOnWorkspace;
            case confirmTypes.OrderPlaceAlert:
                return BaseSettings.confirmPlaceAlert;
            case confirmTypes.AlertCancel:
                return BaseSettings.confirmCancelAlert;
            case confirmTypes.PositionSizeCalculatorInfoWindow:
                return BaseSettings.positionSizeCalculatorInfoWindow;
            case confirmTypes.SubscriptionsAndUnsubscriptions:
                return BaseSettings.confirmSubscriptions;
            case confirmTypes.CopyTradingStartStopFollow:
                return BaseSettings.confirmAccountFollowing;
        }
        throw new Error('WDGeneralSettingsUtils Not supported');
    }
    needToShowWarning(confirmData = undefined) {
        // some warnings ignore the warnOnWrongOrder setting #110331
        return BaseSettings.warnIfWrongOrder || (confirmData === null || confirmData === void 0 ? void 0 : confirmData.needShowWarningForce);
    }
    get selectedTradingSymbol() {
        const symbolID = BaseSettings.tradingSymbolID;
        if (!isNullOrUndefined(symbolID)) {
            const foundSymbol = DataCache.getInstrumentByName(symbolID);
            if (!isNullOrUndefined(foundSymbol)) {
                return foundSymbol;
            }
        }
        const firstSymbol = DataCache.getFirstTradableInstrument();
        return firstSymbol;
    }
    set selectedTradingSymbol(symbolID) {
        if (!isNullOrUndefined(symbolID)) {
            BaseSettings.tradingSymbolID = symbolID;
        }
    }
    existTradingAccountIdOrFirst(accountID) {
        if (!isNullOrUndefined(accountID)) {
            const foundAccount = DataCache.GetAccountById(accountID);
            if (!isNullOrUndefined(foundAccount)) {
                return foundAccount.AcctNumber;
            }
        }
        const firstAccount = DataCache.getAccountFirst();
        if (!isNullOrUndefined(firstAccount)) {
            return firstAccount.AcctNumber;
        }
        return null;
    }
    get selectedTradingAccount() {
        const accountID = BaseSettings.tradingAccountID;
        if (!isNullOrUndefined(accountID)) {
            const foundAccount = DataCache.GetAccountById(accountID);
            if (!isNullOrUndefined(foundAccount)) {
                return foundAccount;
            }
        }
        const firstAccount = DataCache.getAccountFirst();
        if (!isNullOrUndefined(firstAccount)) {
            return firstAccount;
        }
        return null;
    }
    set selectedTradingAccount(accountID) {
        BaseSettings.tradingAccountID = accountID;
    }
    IsTicksFractionalForForex() {
        return BaseSettings.offsetType === OffsetModeViewEnum.TicksFractionalForForex;
    }
    isAllowOTHTradingByDefault(account, instrument) {
        if (isNullOrUndefined(account) || isNullOrUndefined(instrument)) {
            return null;
        }
        if (!account.isAllowOTHTrading(instrument)) {
            return null;
        }
        return BaseSettings.isAllowOTHTrading;
    }
}
